import React, { useMemo } from 'react';
import { Box, Typography, Stack, Avatar, IconButton, Tooltip, Skeleton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import dayjs from 'dayjs';

import { DASH, EVENT_TONES, PRESENCE_TONES, resolvePhotoUrl, getInitials } from '../dashboard/dashboardTokens';
import { EmptyState } from '../dashboard/Surface';

const HOUR = 60;
const DAY = 24 * HOUR;

// Rovnaké pravidlo ako v detaile: príchod a odchod v tej istej minúte nie je
// smena, je to omylom dvakrát stlačené tlačidlo.
const MIN_SHIFT_MINUTES = 5;

const DAYJS_LOCALE = { SK: 'sk', EN: 'en', CZ: 'cs' };

const minutesOf = (value) => {
    if (!value) return null;
    const d = dayjs(value);
    return d.isValid() ? d.hour() * HOUR + d.minute() : null;
};

// Trvanie počítame z celých časových značiek so sekundami a orezávame nadol —
// presne ako TIMESTAMPDIFF(MINUTE) na backende. Odčítanie minút v dni by inak
// dalo o minútu iné číslo než detail zamestnanca a čísla by si protirečili.
const spanBetween = (from, to) => {
    if (!from || !to) return null;
    const ms = new Date(to) - new Date(from);
    return Number.isFinite(ms) && ms > 0 ? Math.floor(ms / 60000) : 0;
};

const clockLabel = (minutes) => {
    if (minutes == null) return null;
    const m = ((Math.round(minutes) % DAY) + DAY) % DAY;
    return `${String(Math.floor(m / HOUR)).padStart(2, '0')}:${String(m % HOUR).padStart(2, '0')}`;
};

const formatSpan = (minutes) => {
    const total = Math.round(minutes || 0);
    if (total <= 0) return null;
    const h = Math.floor(total / HOUR);
    const m = total % HOUR;
    if (h === 0) return `${m} m`;
    return m === 0 ? `${h} h` : `${h} h ${m} m`;
};

/**
 * Z jedného dňa dochádzky spraví riadok: kedy prišiel, kedy odišiel, koľko
 * odrobil a kde bol.
 */
const buildRow = (emp) => {
    const records = [...(emp.attendance_records || [])]
        .filter((r) => r.start_time)
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

    let netMinutes = 0;
    let runningMinutes = 0;
    let breakMinutes = 0;
    let openShift = false;
    let onBreak = false;
    let autoEnded = false;
    let statusName = null;

    records.forEach((record) => {
        const duration = spanBetween(record.start_time, record.end_time);
        const trivial = duration != null && duration < MIN_SHIFT_MINUTES;

        let recordBreaks = 0;
        (record.breaks || []).forEach((slot) => {
            if (!slot.break_start_time) return;
            if (!slot.break_end_time) {
                onBreak = true;
                return;
            }
            recordBreaks += spanBetween(slot.break_start_time, slot.break_end_time) || 0;
        });

        // Kto je práve v práci, má odpracovaný čas tiež — len ešte nie konečný.
        // Prázdna bunka pri celej skupine „v práci" vyzerá ako chyba.
        if (!record.end_time) {
            openShift = true;
            const since = spanBetween(record.start_time, new Date());
            if (since != null) runningMinutes += Math.max(since - recordBreaks, 0);
        }
        // Auto-ukončenú smenu zapísal systém svojím časom, nie človek svojím
        // odchodom — do odpracovaného času nevstupuje, rovnako ako v detaile.
        const isAuto = record.end_time && (!record.scan_type_end || record.scan_type_end === 'auto');
        if (isAuto) autoEnded = true;

        if (!trivial) {
            breakMinutes += recordBreaks;
            if (duration != null && !isAuto) {
                netMinutes += Math.max(duration - recordBreaks, 0);
            }
        }

        (record.statuses || []).forEach((s) => {
            if (!statusName) statusName = s.status_name;
        });
    });

    const counted = records.filter((r) => {
        if (!r.end_time) return Boolean(r.start_time);
        return (spanBetween(r.start_time, r.end_time) || 0) >= MIN_SHIFT_MINUTES;
    });

    const realOuts = counted
        .filter((r) => r.end_time && r.scan_type_end && r.scan_type_end !== 'auto')
        .map((r) => {
            const from = minutesOf(r.start_time);
            const rawTo = minutesOf(r.end_time);
            return rawTo < from ? rawTo + DAY : rawTo;
        });

    return {
        emp,
        firstIn: counted.length ? minutesOf(counted[0].start_time) : null,
        lastOut: realOuts.length ? Math.max(...realOuts) : null,
        netMinutes,
        runningMinutes,
        breakMinutes,
        openShift,
        onBreak: onBreak && openShift,
        autoEnded,
        statusName,
        workplace: records.find((r) => r.workplace_name)?.workplace_name || null,
        hasDay: records.length > 0,
    };
};

// Poradie skupín kopíruje to, ako sa na deň pozerá zamestnávateľ: najprv kto
// je práve v práci, nakoniec kto sa vôbec neukázal.
const GROUPS = [
    { key: 'working', tone: PRESENCE_TONES.working, match: (r) => r.openShift && !r.onBreak },
    { key: 'onBreak', tone: PRESENCE_TONES.onBreak, match: (r) => r.onBreak },
    { key: 'done', tone: EVENT_TONES.departure, match: (r) => !r.openShift && r.hasDay },
    { key: 'away', tone: null, match: (r) => !r.hasDay },
];

const groupKeyOf = (row) => (GROUPS.find((g) => g.match(row)) || GROUPS[3]).key;

/**
 * Zhrnutie dňa pre nadpis stránky. Počíta sa z tých istých riadkov, aké vidno
 * v zozname, nech nadpis a obsah nemôžu tvrdiť každý niečo iné.
 */
export const summarizeDay = (employees) => {
    const rows = (employees || []).map(buildRow);
    const counts = { working: 0, onBreak: 0, done: 0, away: 0 };
    let totalMinutes = 0;
    rows.forEach((row) => {
        counts[groupKeyOf(row)] += 1;
        totalMinutes += row.netMinutes + row.runningMinutes;
    });
    return { ...counts, totalMinutes, totalWorkedStr: formatSpan(totalMinutes) };
};

/**
 * Predel skupiny — tichý pás, nie plávajúci nadpis. Riadky sú oddelené
 * vlasovou linkou, takže vzdušný nadpis medzi nimi nevyzeral ako predel, ale
 * ako ďalší riadok. Farebná bodka je tu raz na skupinu namiesto raz na riadok.
 */
const GroupHeader = ({ groupKey, tone, count, t }) => (
    <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
            px: { xs: 2, sm: 2.5 },
            py: 0.875,
            bgcolor: 'action.hover',
            borderTop: '1px solid',
            borderColor: 'divider',
        }}
    >
        <Box
            sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor: tone || 'transparent',
                border: tone ? 'none' : '1.5px solid',
                borderColor: 'text.disabled',
            }}
        />
        <Typography
            sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'text.secondary',
            }}
        >
            {t(`pages.employees.list.group.${groupKey}`)}
        </Typography>
        <Typography
            sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.disabled', ...DASH.numeric }}
        >
            {count}
        </Typography>
    </Stack>
);

// Odkedy má zmysel spomenúť appku zvlášť. Kým chodí do práce, sedí to na deň;
// až väčší odstup hovorí niečo nové — appku používa, ale nepípa si.
const APP_GAP_DAYS = 7;

const shortDate = (iso, locale) => {
    const when = dayjs(iso).locale(locale);
    return when.format(when.isSame(dayjs(), 'year') ? 'D. M.' : 'D. M. YYYY');
};

/**
 * Veta pre človeka, ktorý v ten deň nemá dochádzku. Prázdna bunka by nechala
 * zamestnávateľa hádať, či človek len nebol v práci, alebo appku nikdy
 * neotvoril — to sú dve úplne iné veci.
 *
 * Dátumy sú tiež dva a nesmú sa zliať do jedného „naposledy": posledná
 * dochádzka a posledné ozvanie appky vedia byť pol roka od seba.
 */
const AbsenceNote = ({ insight, loading, locale, t }) => {
    if (loading) return <Skeleton width={170} />;

    const workedAt = insight?.everWorked && insight?.lastSeenAt ? dayjs(insight.lastSeenAt) : null;
    const appAt = insight?.appLastSeenAt ? dayjs(insight.appLastSeenAt) : null;
    // Appku pripíšeme, len keď hovorí niečo iné než dochádzka.
    const appNotable = appAt && (!workedAt || appAt.diff(workedAt, 'day') >= APP_GAP_DAYS);
    const appPart = appNotable
        ? t('pages.employees.list.appSeen').replace('{date}', shortDate(insight.appLastSeenAt, locale))
        : null;

    if (workedAt) {
        return (
            <Typography variant="body2" color="text.secondary" noWrap>
                {[
                    t('pages.employees.list.lastSeen').replace('{date}', shortDate(insight.lastSeenAt, locale)),
                    appPart,
                ].filter(Boolean).join('  ·  ')}
            </Typography>
        );
    }

    // Zariadenie má, dochádzku žiadnu — appku si nainštaloval, ale nepípol si ani raz.
    const key = insight?.hasDevices ? 'noAttendanceYet' : 'neverUsed';
    return (
        <Typography variant="body2" color="text.disabled" noWrap>
            {[t(`pages.employees.list.${key}`), appPart].filter(Boolean).join('  ·  ')}
        </Typography>
    );
};

/**
 * Hodnota riadka vpravo — koľko človek odrobil a v akom okne. Tvar má každý
 * riadok rovnaký, aj keď je hodnota prázdna; štyri stĺpce sa pri polovici ľudí
 * bez dochádzky nemali čím naplniť a zoznam pôsobil deravo.
 */
const RowValue = ({ row, t }) => {
    if (row.statusName && !row.hasDay) {
        return (
            <Typography variant="body2" color="text.secondary" noWrap>
                {row.statusName}
            </Typography>
        );
    }
    if (!row.hasDay) return null;

    const running = row.openShift && row.runningMinutes > 0;
    const amount = running ? formatSpan(row.runningMinutes) : formatSpan(row.netMinutes);

    const endLabel = row.lastOut != null
        ? clockLabel(row.lastOut)
        : row.openShift
            ? t('pages.employeeProfile.strip.open').toLowerCase()
            : row.autoEnded ? t('pages.employeeProfile.day.noEndShort').toLowerCase() : '–';

    return (
        <Box sx={{ textAlign: 'right', minWidth: 0 }}>
            <Typography
                sx={{
                    fontFamily: DASH.display,
                    fontSize: '1.0625rem',
                    fontWeight: 600,
                    lineHeight: 1.2,
                    letterSpacing: '-0.015em',
                    color: amount ? 'text.primary' : 'text.disabled',
                    ...DASH.numeric,
                }}
                noWrap
            >
                {amount || '–'}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.125, ...DASH.numeric }}
                noWrap
            >
                {`${clockLabel(row.firstIn) || '–'} → ${endLabel}`}
            </Typography>
        </Box>
    );
};

/**
 * Zoznam zamestnancov za vybraný deň. Jeden riadok = jeden človek, vľavo kto,
 * vpravo koľko — ako riadok vo výpise z účtu. Karta na osobu ukázala to isté,
 * ale zabrala celú obrazovku a pri tridsiatich ľuďoch sa nedalo porovnať nič.
 */
const EmployeeDayList = ({
    employees, insightsByEmployeeId, insightsLoading, loading,
    t, language, onOpen, onNotify, emptyTitle, emptyHint,
}) => {
    const locale = DAYJS_LOCALE[language] || 'sk';
    const rows = useMemo(() => (employees || []).map(buildRow), [employees]);

    // Zoskupenie podľa stavu, nie plochý zoznam: z 35 ľudí má dnes záznam osem
    // a bez skupín sa strácajú medzi dvadsiatimi siedmimi riadkami „naposledy…".
    // Zvolené zoradenie platí vnútri skupiny.
    const groups = useMemo(() => GROUPS
        .map((group) => ({ ...group, rows: rows.filter((row) => groupKeyOf(row) === group.key) }))
        .filter((group) => group.rows.length > 0), [rows]);

    // Keď má celá firma jedno pracovisko, jeho názov pri každom mene nič
    // nerozlišuje — je to len tridsaťkrát zopakovaný riadok textu.
    const showWorkplace = useMemo(
        () => new Set(rows.map((row) => row.workplace).filter(Boolean)).size > 1,
        [rows]
    );

    if (loading) {
        return (
            <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack spacing={1.5}>
                    {Array.from({ length: 8 }, (_, i) => (
                        <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: `${DASH.radiusSm}px` }} />
                    ))}
                </Stack>
            </Box>
        );
    }

    if (!rows.length) {
        return (
            <Box sx={{ p: { xs: 3, sm: 5 } }}>
                <EmptyState title={emptyTitle} hint={emptyHint} />
            </Box>
        );
    }

    return (
        <Box>
            {groups.map((group) => (
                <Box key={group.key}>
                    <GroupHeader groupKey={group.key} tone={group.tone} count={group.rows.length} t={t} />

                    {group.rows.map((row) => {
                        const { emp } = row;
                        const insight = insightsByEmployeeId?.[String(emp.id)];
                        const photo = resolvePhotoUrl(emp.profile_photo_url);

                        // Druhý riadok nesie to, čo je pri tomto človeku dnes
                        // podstatné: kde bol, alebo odkedy sa neukázal.
                        const subtitle = row.hasDay
                            ? [emp.position, showWorkplace ? row.workplace : null].filter(Boolean).join('  ·  ')
                            : null;

                        return (
                            <Box
                                key={emp.id}
                                onClick={() => onOpen(emp)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        onOpen(emp);
                                    }
                                }}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 2,
                                    px: { xs: 2, sm: 2.5 },
                                    py: 1.25,
                                    cursor: 'pointer',
                                    borderTop: '1px solid',
                                    borderColor: 'divider',
                                    transition: 'background-color .12s ease',
                                    '&:hover': { bgcolor: 'action.hover' },
                                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: -2 },
                                }}
                            >
                                <Avatar
                                    src={photo || undefined}
                                    alt={emp.name}
                                    sx={{ width: 36, height: 36, fontSize: '0.8125rem', fontWeight: 600, flexShrink: 0 }}
                                >
                                    {getInitials(emp.name)}
                                </Avatar>

                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, lineHeight: 1.35 }} noWrap>
                                        {emp.name}
                                    </Typography>
                                    {subtitle ? (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                                            {subtitle}
                                        </Typography>
                                    ) : (
                                        <AbsenceNote insight={insight} loading={insightsLoading} locale={locale} t={t} />
                                    )}
                                </Box>

                                <RowValue row={row} t={t} />

                                <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                                    <Tooltip title={t('pages.employees.list.notify')} arrow disableInteractive>
                                        <span>
                                            <IconButton
                                                size="small"
                                                disabled={!emp.has_fcm_token}
                                                onClick={(e) => { e.stopPropagation(); onNotify(emp); }}
                                                aria-label={t('pages.employees.list.notify')}
                                            >
                                                <EmailOutlinedIcon sx={{ fontSize: 17 }} />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t('pages.employeeProfile.openPage')} arrow disableInteractive>
                                        <IconButton
                                            size="small"
                                            component={RouterLink}
                                            to={`/employees/${emp.id}`}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={t('pages.employeeProfile.openPage')}
                                        >
                                            <OpenInNewRounded sx={{ fontSize: 17 }} />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
};

export default EmployeeDayList;
